import { useCallback, useEffect, useMemo, useRef } from 'react';
import { playButtonSound } from '@wg/web2clientapi/sound';

import settings from '~/settings';
import { useAppDispatch, useAppSelector } from '~/store';
import { clanLadderSelector, clanSelector } from '~/store/selectors/clanSelectors';
import { clanWarsCurrentStageSelector } from '~/store/selectors/clanWarsSelectors';
import {
  currentClanIdSelector,
  currentRoleNameSelector,
  isLoggedInSelector,
} from '~/store/selectors/currentAccountSelector';
import { navigationSelector } from '~/store/selectors/navigationSelectors';
import { ENTRY_SOURCE } from '~/store/slices/settingsSlice';
import { initClanWarsThunk } from '~/store/thunks/clanWarsThunks';
import { fetchClan } from '~/Actions/ActionClanProfile';

import ClanHeader from '~/UIKit/ClanHeader/ClanHeader';
import ClansInformerButton from '~/UIKit/ClansInformerButton/ClansInformerButton';

import styles from './ClanHub.module.scss';
import ClanHubTile, { CLAN_HUB_TILE_SIZE } from './ClanHubTile/ClanHubTile';
import ClanHubTilesActivities from './ClanHubTilesActivities';
import { buildHubTiles } from './clanHub.tiles.build';
import { HUB_TILES_PRESET, HUB_TILES_PRESETS } from './clanHub.tiles.config';

const ClanHub = () => {
  const dispatch = useAppDispatch();

  const clanId = useAppSelector(currentClanIdSelector);
  const currentRoleName = useAppSelector(currentRoleNameSelector);
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const clanWarsCurrentStage = useAppSelector(clanWarsCurrentStageSelector);

  const clan = useAppSelector((state) => {
    return clanId ? clanSelector(state, clanId) : null;
  });

  const isWowsLadderEnabled = settings.ladder?.isEnabled ?? false;

  const clanWowsLadder = useAppSelector((state) => {
    return clanId && isWowsLadderEnabled ? clanLadderSelector(state, clanId) : null;
  });

  // Ref to store clan-related data for use in effects without adding them to dependencies
  const clanDataRef = useRef({ clan, clanWowsLadder, isWowsLadderEnabled });
  clanDataRef.current = { clan, clanWowsLadder, isWowsLadderEnabled };

  const navigationContext = useAppSelector(navigationSelector);

  const clanHubPreset = useMemo(() => {
    if (clanId) {
      return HUB_TILES_PRESET.PLAYER_WITH_CLAN;
    }

    if (isLoggedIn) {
      return HUB_TILES_PRESET.PLAYER_WITHOUT_CLAN;
    }

    return HUB_TILES_PRESET.NOT_LOGGED_IN;
  }, [clanId, isLoggedIn]);

  const { tilesMain, tilesActivities } = useMemo(() => {
    return {
      tilesMain: buildHubTiles(HUB_TILES_PRESETS[clanHubPreset].MAIN, navigationContext),
      tilesActivities: buildHubTiles(HUB_TILES_PRESETS[clanHubPreset].ASIDE, navigationContext),
    };
  }, [navigationContext, clanHubPreset]);

  useEffect(() => {
    if (!clanId) {
      return;
    }

    const { clan, clanWowsLadder, isWowsLadderEnabled } = clanDataRef.current;
    if (!clan || (isWowsLadderEnabled && !clanWowsLadder)) {
      void dispatch(fetchClan(clanId));
    }
  }, [clanId, dispatch]);

  useEffect(() => {
    if (!clanId) {
      return;
    }

    if (!clanWarsCurrentStage) {
      void dispatch(initClanWarsThunk(false));
    }
  }, [clanId, clanWarsCurrentStage, dispatch]);

  const handleTileClick = useCallback(() => {
    void playButtonSound();
  }, []);

  return (
    <div className={styles.clanHub}>
      {!!clan && (
        <>
          <ClansInformerButton source={ENTRY_SOURCE.CLAN_HUB} />
          <ClanHeader className={styles.clanHeader} clan={clan} isOwnClanBase />
        </>
      )}

      <div className={styles.hubTilesContainer}>
        <div className={styles.hubTilesLarge}>
          {tilesMain.map((tile) => (
            <ClanHubTile
              tile={tile}
              size={CLAN_HUB_TILE_SIZE.LARGE}
              isClanMember={!!clanId}
              currentRoleName={currentRoleName}
              onClick={handleTileClick}
              key={tile.id}
            />
          ))}
        </div>
        <ClanHubTilesActivities
          tiles={tilesActivities}
          isClanMember={!!clanId}
          currentRoleName={currentRoleName}
          onClick={handleTileClick}
        />
      </div>
    </div>
  );
};

export default ClanHub;
